/**
 * Customer Action Modals
 * Provides modal dialogs for customer quick actions
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

// Types
interface CustomerActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  customerId: string;
}

interface AddNoteModalProps extends CustomerActionModalProps {
  onSave?: (data: NoteFormData) => void;
}

interface CreateDisputeModalProps extends CustomerActionModalProps {
  outstandingAmount?: number;
  currency?: string;
  onSave?: (data: DisputeFormData) => void;
}

export interface NoteFormData {
  content: string;
}

export interface DisputeFormData {
  invoiceNumber: string;
  disputeAmount: number;
  reason: string;
  description: string;
  priority: string;
}

/**
 * Add Note Modal
 */
export function AddNoteModal({ open, onOpenChange, customerName, customerId, onSave }: AddNoteModalProps) {
  const [content, setContent] = useState('');

  const handleSave = () => {
    // Validation
    if (!content.trim()) {
      toast.error('Please enter note content');
      return;
    }

    const formData: NoteFormData = {
      content,
    };

    if (onSave) {
      onSave(formData);
    } else {
      toast.success('Note added successfully');
    }

    // Reset form
    setContent('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
          <DialogDescription>Add a note for {customerName}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer</Label>
            <Input id="customer" value={customerName} disabled />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="content">Note Content *</Label>
            <Textarea
              id="content"
              placeholder="Enter note content..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Note</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Create Dispute Modal
 */
export function CreateDisputeModal({
  open,
  onOpenChange,
  customerName,
  customerId,
  outstandingAmount = 0,
  currency = 'GBP',
  onSave,
}: CreateDisputeModalProps) {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [disputeAmount, setDisputeAmount] = useState('');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');

  const handleSave = () => {
    // Validation
    if (!invoiceNumber.trim()) {
      toast.error('Please enter an invoice number');
      return;
    }
    if (!disputeAmount || parseFloat(disputeAmount) <= 0) {
      toast.error('Please enter a valid dispute amount');
      return;
    }
    if (!reason) {
      toast.error('Please select a dispute reason');
      return;
    }
    if (!description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    const formData: DisputeFormData = {
      invoiceNumber,
      disputeAmount: parseFloat(disputeAmount),
      reason,
      description,
      priority,
    };

    if (onSave) {
      onSave(formData);
    } else {
      toast.success('Dispute created successfully');
    }

    // Reset form
    setInvoiceNumber('');
    setDisputeAmount('');
    setReason('');
    setDescription('');
    setPriority('medium');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create Dispute</DialogTitle>
          <DialogDescription>Create a new dispute for {customerName}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer</Label>
            <Input id="customer" value={customerName} disabled />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="invoice-number">
              Invoice Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="invoice-number"
              placeholder="Enter invoice number"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="dispute-amount">
              Dispute Amount <span className="text-red-500">*</span>
            </Label>
            <Input
              id="dispute-amount"
              type="number"
              placeholder="0.00"
              value={disputeAmount}
              onChange={(e) => setDisputeAmount(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="reason">
              Dispute Reason <span className="text-red-500">*</span>
            </Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="reason">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pricing">Pricing Discrepancy</SelectItem>
                <SelectItem value="quality">Quality Issue</SelectItem>
                <SelectItem value="delivery">Delivery Problem</SelectItem>
                <SelectItem value="not_received">Goods Not Received</SelectItem>
                <SelectItem value="duplicate">Duplicate Invoice</SelectItem>
                <SelectItem value="credit_note">Credit Note Expected</SelectItem>
                <SelectItem value="payment_made">Payment Already Made</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="priority">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger id="priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="description">
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              placeholder="Provide details about the dispute..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Create Dispute</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
